// Iter 82.8 Sprint 2 / Phase E3.0 — Salary Components Master.
// CRUD UI for atomic pay heads with formula tester + optional GL/CC
// mappings (operator additions: gl_account_code, gl_account_name,
// cost_centre).
import { useCallback, useEffect, useState } from "react";
import {
    Plus, PencilSimple, TrashSimple, MagnifyingGlass,
    Flask, X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { buildComponentNameMap, prettyFormula, formulaTooltip } from "../../lib/formulaFormat";

const NATURE_TONE = {
    earning: "bg-emerald-100 text-emerald-800",
    deduction: "bg-rose-100 text-rose-800",
    employer_contribution: "bg-violet-100 text-violet-800",
    reimbursement: "bg-sky-100 text-sky-800",
};

const TAX_TONE = {
    fully_taxable: "bg-amber-100 text-amber-800",
    partially_exempt: "bg-sky-100 text-sky-800",
    fully_exempt: "bg-emerald-100 text-emerald-800",
};

const EMPTY_FORM = {
    code: "", name: "", nature: "earning", taxability: "fully_taxable",
    exemption_section: "", formula_type: "fixed_amount", formula_input: "0",
    monthly_cap: "", annual_cap: "",
    is_statutory: false, is_active: true, sequence_no: 100,
    description: "",
    gl_account_code: "", gl_account_name: "", cost_centre: "",
};

export default function PayrollComponents() {
    const { can } = useAuth();
    const canEdit = can("payroll:admin");
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [natureFilter, setNatureFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formulaSample, setFormulaSample] = useState(null);
    const [formulaError, setFormulaError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, m] = await Promise.all([
                api.get("/payroll/component/list", { params: natureFilter ? { nature: natureFilter } : {} }),
                api.get("/payroll/component/meta"),
            ]);
            setItems(list.data.items);
            setMeta(m.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load components"));
        } finally {
            setLoading(false);
        }
    }, [natureFilter]);

    useEffect(() => { reload(); }, [reload]);

    const filteredItems = items.filter((i) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (i.code || "").toLowerCase().includes(q) || (i.name || "").toLowerCase().includes(q);
    });

    const openNew = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormulaSample(null);
        setFormulaError(null);
        setOpen(true);
    };
    const openEdit = (it) => {
        setEditing(it);
        setForm({
            ...it,
            monthly_cap: it.monthly_cap ?? "",
            annual_cap: it.annual_cap ?? "",
        });
        setFormulaSample(null);
        setFormulaError(null);
        setOpen(true);
    };
    const testFormula = async () => {
        // Iter 111.1 UAT · Obs 2 — Engine-backed formulas are computed
        // by dedicated engine subsystems (TDS/PT/ESI/Gratuity) at
        // payroll run time; they cannot be tested via the generic DSL
        // evaluator. Consult the central engine-marker registry
        // (backend `/payroll/component/meta.engine_markers`) so the
        // UI does not carry a fragile local whitelist.
        const trimmed = (form.formula_input || "").trim();
        const engineMarkers = meta?.engine_markers || [];
        if (engineMarkers.includes(trimmed)) {
            setFormulaSample(null);
            setFormulaError(
                "This component is calculated by the payroll engine "
                + "during payroll processing and cannot be tested "
                + "independently."
            );
            return;
        }
        try {
            const { data } = await api.post("/payroll/component/validate-formula",
                { formula: form.formula_input });
            setFormulaSample(data.sample_value);
            setFormulaError(null);
        } catch (err) {
            setFormulaSample(null);
            setFormulaError(formatApiError(err, "Formula invalid"));
        }
    };
    const save = async () => {
        // Client-side required-field check: prevents the bare 400 round-trip
        // that left operators staring at an empty dialog with no feedback.
        if (!form.code || !form.code.trim()) {
            setFormulaError("Code is required");
            return;
        }
        if (!form.name || !form.name.trim()) {
            setFormulaError("Name is required");
            return;
        }
        if (!form.formula_input || !form.formula_input.trim()) {
            setFormulaError("Formula / Value is required");
            return;
        }
        try {
            const payload = {
                ...form,
                monthly_cap: form.monthly_cap === "" ? null : Number(form.monthly_cap),
                annual_cap: form.annual_cap === "" ? null : Number(form.annual_cap),
                sequence_no: Number(form.sequence_no),
            };
            if (editing) {
                await api.patch(`/payroll/component/${editing.id}`, payload);
                toast.success(`Updated component ${form.code}`);
            } else {
                await api.post("/payroll/component", payload);
                toast.success(`Created component ${form.code}`);
            }
            setOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const remove = async (it) => {
        if (!window.confirm(`Delete component ${it.code}?`)) return;
        try {
            await api.delete(`/payroll/component/${it.id}`);
            toast.success(`Deleted ${it.code}`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="payroll-comp-page">
            <PageHeader
                eyebrow="Payroll Masters"
                title="Salary Components"
                description={`${items.length} atomic pay-head${items.length === 1 ? "" : "s"} · canonical list seeded`}
                actions={(
                    canEdit ? (
                        <Button onClick={openNew} size="sm" data-testid="comp-new-btn">
                            <Plus size={14} className="mr-1" /> New Component
                        </Button>
                    ) : null
                )}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="comp-filters">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Search</Label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                className="pl-9" placeholder="Code or name…"
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                data-testid="comp-search"
                            />
                        </div>
                    </div>
                    <div className="w-48">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Nature</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2"
                                value={natureFilter} onChange={(e) => setNatureFilter(e.target.value)}
                                data-testid="comp-nature-filter">
                            <option value="">All</option>
                            {(meta?.natures || []).map((n) => <option key={n} value={n}>{n.replaceAll("_", " ")}</option>)}
                        </select>
                    </div>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="comp-table">
                {loading ? (
                    <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
                ) : (
                    <table className="w-full text-sm min-w-[1100px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Seq</th>
                                <th className="text-left px-3 py-2 font-medium">Code</th>
                                <th className="text-left px-3 py-2 font-medium">Name</th>
                                <th className="text-left px-3 py-2 font-medium">Nature</th>
                                <th className="text-left px-3 py-2 font-medium">Taxability</th>
                                <th className="text-left px-3 py-2 font-medium">Formula (readable)</th>
                                <th className="text-left px-3 py-2 font-medium">GL Code</th>
                                <th className="text-left px-3 py-2 font-medium">Cost Centre</th>
                                <th className="text-left px-3 py-2 font-medium">Flags</th>
                                <th className="text-left px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                const nameMap = buildComponentNameMap(items);
                                return filteredItems.map((c) => (
                                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`comp-row-${c.code}`}>
                                    <td className="px-3 py-2 text-xs font-mono text-slate-500">{c.sequence_no}</td>
                                    <td className="px-3 py-2 font-mono font-semibold text-slate-800">{c.code}</td>
                                    <td className="px-3 py-2 text-sm">{c.name}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${NATURE_TONE[c.nature] || ""}`}>{c.nature.replaceAll("_", " ")}</Badge>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${TAX_TONE[c.taxability] || ""}`}>{c.taxability.replaceAll("_", " ")}</Badge>
                                    </td>
                                    <td className="px-3 py-2 max-w-[260px]">
                                        <div className="text-xs text-slate-700 truncate" title={formulaTooltip(c.formula_input, nameMap)} data-testid={`comp-formula-pretty-${c.code}`}>
                                            {prettyFormula(c.formula_input, nameMap) || <span className="text-slate-400">—</span>}
                                        </div>
                                        {c.formula_input && prettyFormula(c.formula_input, nameMap) !== c.formula_input && (
                                            <div className="font-mono text-[9px] text-slate-400 truncate">Raw: {c.formula_input}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs font-mono">{c.gl_account_code || "—"}</td>
                                    <td className="px-3 py-2 text-xs">{c.cost_centre || "—"}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {c.is_statutory && <Badge className="bg-rose-50 text-rose-700 text-[10px] mr-1">Stat</Badge>}
                                        {c.is_system_managed && <Badge className="bg-slate-100 text-slate-600 text-[10px] mr-1">Sys</Badge>}
                                        {c.is_in_use && <Badge className="bg-amber-100 text-amber-700 text-[10px] mr-1">In-Use</Badge>}
                                        {!c.is_active && <Badge className="bg-zinc-100 text-zinc-600 text-[10px]">Inactive</Badge>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            {canEdit ? (
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`comp-edit-${c.code}`}>
                                                    <PencilSimple size={12} />
                                                </Button>
                                            ) : (
                                                <span className="text-[10px] text-slate-400 italic">Read-only</span>
                                            )}
                                            {canEdit && !c.is_system_managed && (
                                                <Button variant="ghost" size="sm" onClick={() => remove(c)} data-testid={`comp-del-${c.code}`}>
                                                    <TrashSimple size={12} className="text-rose-600" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                ));
                            })()}
                            {filteredItems.length === 0 && (
                                <tr><td colSpan={10} className="text-center py-12 text-sm text-slate-400">No components match.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl" data-testid="comp-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit ${editing.code}` : "New Component"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Code (UPPERCASE_SNAKE)" testid="comp-form-code">
                            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                   disabled={!!editing} data-testid="comp-form-code-input" />
                        </Field>
                        <Field label="Name" testid="comp-form-name">
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </Field>
                        <Field label="Nature" testid="comp-form-nature">
                            <select className="w-full border rounded-md text-sm h-9 px-2"
                                    value={form.nature} onChange={(e) => setForm({ ...form, nature: e.target.value })}>
                                {(meta?.natures || []).map((n) => <option key={n} value={n}>{n.replaceAll("_", " ")}</option>)}
                            </select>
                        </Field>
                        <Field label="Taxability" testid="comp-form-tax">
                            <select className="w-full border rounded-md text-sm h-9 px-2"
                                    value={form.taxability} onChange={(e) => setForm({ ...form, taxability: e.target.value })}>
                                {(meta?.taxabilities || []).map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
                            </select>
                        </Field>
                        <Field label="Formula Type" testid="comp-form-ftype">
                            <select className="w-full border rounded-md text-sm h-9 px-2"
                                    value={form.formula_type} onChange={(e) => setForm({ ...form, formula_type: e.target.value })}>
                                {(meta?.formula_types || []).map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
                            </select>
                        </Field>
                        <Field label="Sequence" testid="comp-form-seq">
                            <Input type="number" value={form.sequence_no}
                                   onChange={(e) => setForm({ ...form, sequence_no: Number(e.target.value) })} />
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label="Formula / Value" testid="comp-form-formula">
                                <div className="flex gap-2">
                                    <Input value={form.formula_input}
                                           onChange={(e) => setForm({ ...form, formula_input: e.target.value })}
                                           placeholder="e.g. min(0.12 * BASIC, 1800)"
                                           className="font-mono text-xs"
                                           data-testid="comp-form-formula-input" />
                                    <Button type="button" variant="outline" size="sm" onClick={testFormula}
                                            data-testid="comp-form-test-btn">
                                        <Flask size={12} className="mr-1" weight="fill" /> Test
                                    </Button>
                                </div>
                                {formulaSample !== null && (
                                    <div className="mt-2 text-xs text-emerald-700 font-mono" data-testid="comp-form-sample">
                                        Sample value: {formulaSample}
                                    </div>
                                )}
                                {formulaError && (
                                    <div className="mt-2 text-xs text-rose-700" data-testid="comp-form-error">{formulaError}</div>
                                )}
                                {/* Iter 111.1 · Obs 14 — Readable formula preview. Codes remain stable in storage. */}
                                {form.formula_input && (() => {
                                    const nameMap = buildComponentNameMap(items);
                                    const pretty = prettyFormula(form.formula_input, nameMap);
                                    if (pretty === form.formula_input) return null;
                                    return (
                                        <div className="mt-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5" data-testid="comp-form-formula-pretty">
                                            <span className="text-[10px] uppercase text-slate-500 tracking-wider mr-2">Reads as</span>
                                            <span className="italic">{pretty}</span>
                                        </div>
                                    );
                                })()}
                                <div className="mt-2 text-[10px] text-slate-500">
                                    Variables: {(meta?.dsl?.variables || []).join(", ")} · Functions: {(meta?.dsl?.functions || []).join(", ")}
                                </div>
                            </Field>
                        </div>
                        <Field label="Monthly Cap" testid="comp-form-mcap">
                            <Input type="number" value={form.monthly_cap}
                                   onChange={(e) => setForm({ ...form, monthly_cap: e.target.value })}
                                   placeholder="(no cap)" />
                        </Field>
                        <Field label="Annual Cap" testid="comp-form-acap">
                            <Input type="number" value={form.annual_cap}
                                   onChange={(e) => setForm({ ...form, annual_cap: e.target.value })}
                                   placeholder="(no cap)" />
                        </Field>
                        <Field label="GL Account Code" testid="comp-form-gl">
                            <Input value={form.gl_account_code}
                                   onChange={(e) => setForm({ ...form, gl_account_code: e.target.value })}
                                   placeholder="e.g. 5100" />
                        </Field>
                        <Field label="GL Account Name" testid="comp-form-gl-name">
                            <Input value={form.gl_account_name}
                                   onChange={(e) => setForm({ ...form, gl_account_name: e.target.value })}
                                   placeholder="e.g. Salaries & Wages" />
                        </Field>
                        <Field label="Cost Centre (optional)" testid="comp-form-cc">
                            <Input value={form.cost_centre}
                                   onChange={(e) => setForm({ ...form, cost_centre: e.target.value })}
                                   placeholder="e.g. ENG-IN" />
                        </Field>
                        <Field label="Exemption Section (if any)" testid="comp-form-exempt">
                            <Input value={form.exemption_section}
                                   onChange={(e) => setForm({ ...form, exemption_section: e.target.value })}
                                   placeholder="e.g. 10(13A)" />
                        </Field>
                        <div className="sm:col-span-2 flex gap-4 items-center text-xs">
                            <label className="flex gap-2 items-center">
                                <input type="checkbox" checked={form.is_statutory}
                                       onChange={(e) => setForm({ ...form, is_statutory: e.target.checked })} />
                                Statutory
                            </label>
                            <label className="flex gap-2 items-center">
                                <input type="checkbox" checked={form.is_active}
                                       onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                Active
                            </label>
                        </div>
                        <div className="sm:col-span-2">
                            <Field label="Description" testid="comp-form-desc">
                                <textarea className="w-full border rounded-md text-sm p-2"
                                          rows={2} value={form.description}
                                          onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </Field>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="comp-form-cancel">Cancel</Button>
                        <Button onClick={save} data-testid="comp-form-save">{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <Label className="text-[10px] uppercase text-slate-500 font-semibold">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
